/**
 * Footer.jsx
 * 
 * Shared site footer. Displayed on every page except the explore (globe) page.
 * Clean multi-column layout with brand, navigation, resources, and legal.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { HOMEPAGE_ROUTE, EXPLORER_ROUTE } from '../data/siteConfig.js';

export default function Footer() {
  const resourceLinks = [
    { label: 'Tool Comparisons', path: '/blog', external: false },
    { label: 'Capability Profiles', path: '/database', external: false },
    { label: 'Workflow Builder', path: '/workflow', external: false },
    { label: 'Machine-Readable Center', path: '/resources', external: false },
    { label: 'Sitemap XML', path: '/sitemap.xml', external: true },
    { label: 'LLMs TXT', path: '/llms.txt', external: true },
    { label: 'LLMs Full TXT', path: '/llms-full.txt', external: true },
  ];

  return (
    <footer className="bg-[#030303] border-t border-white/[0.06] mt-24">
      <div className="max-w-7xl mx-auto px-10 pt-24 pb-14">
        
        {/* Multi-column grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-16 mb-20">
          
          {/* Column 1 — Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to={HOMEPAGE_ROUTE} className="inline-block mb-6">
              <span
                className="text-3xl text-white"
                style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif", letterSpacing: '0.05em' }}
              >
                TOUCHSTONE
              </span>
            </Link>
            <p className="text-base text-zinc-500 leading-relaxed mb-8">
              AI tool discovery and workflow planning platform with neutral research, fit evaluation, and workflow guidance.
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="text-xs text-zinc-600 px-3.5 py-1.5 border border-white/[0.06] rounded-full">
                No affiliate links
              </span>
              <span className="text-xs text-zinc-600 px-3.5 py-1.5 border border-white/[0.06] rounded-full">
                No sponsored content
              </span>
              <span className="text-xs text-zinc-600 px-3.5 py-1.5 border border-white/[0.06] rounded-full">
                No rankings
              </span>
            </div>
          </div>

          {/* Column 2 — Navigate */}
          <div>
            <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-widest mb-6">
              Navigate
            </h3>
            <ul className="space-y-4">
              {[
                { label: 'Home', path: HOMEPAGE_ROUTE },
                { label: 'Explore', path: EXPLORER_ROUTE },
                { label: 'Database', path: '/database' },
                { label: 'Workflow', path: '/workflow' },
                { label: 'Blog', path: '/blog' },
              ].map(link => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-base text-zinc-500 hover:text-white transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 — Resources */}
          <div>
            <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-widest mb-6">
              Resources
            </h3>
            <ul className="space-y-4">
              {resourceLinks.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <a
                      href={link.path}
                      className="text-base text-zinc-500 hover:text-white transition-colors duration-200"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.path}
                      className="text-base text-zinc-500 hover:text-white transition-colors duration-200"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 — Legal */}
          <div>
            <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-widest mb-6">
              Legal
            </h3>
            <ul className="space-y-4">
              {[
                { label: 'Privacy Policy', path: '/privacy' },
                { label: 'Terms of Service', path: '/terms' },
                { label: 'Cookie Policy', path: '/cookies' },
                { label: 'Editorial Policy', path: '/editorial-policy' },
                { label: 'Contact', path: '/contact' },
              ].map(link => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className="text-base text-zinc-500 hover:text-white transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar — copyright */}
        <div className="pt-8 border-t border-white/[0.06]">
          <p className="text-sm text-zinc-600">
            &copy; 2026 Touchstone. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
